import {
  Box,
  Divider,
  Paper,
  Typography,
} from '@mui/material';

interface Section {
  title: string;
  body: string[];
  code?: string;
}

const sections: Section[] = [
  {
    title: '1. Login Flow — PAM Authentication',
    body: [
      'The client POSTs { username, password } to POST /api/auth/login.',
      'The backend validates the credentials via PAM (Linux Pluggable Authentication Modules).',
      'On success a short-lived "pending" JWT is returned. The pending token encodes only the username and has a 5-minute TTL.',
      'On 5 consecutive failures within 60 seconds the source IP is banned for 1 hour (HTTP 403).',
    ],
    code: 'POST /api/auth/login  →  { status: "2fa_required" | "setup_required", pendingToken }',
  },
  {
    title: '2. 2FA Verification — TOTP',
    body: [
      'The client POSTs the TOTP code (or recovery code) plus the pending token as a Bearer header to POST /api/auth/2fa/verify.',
      'If the user has no 2FA configured yet, they are directed to /api/auth/2fa/setup first, which returns a QR code and recovery codes.',
      'On successful TOTP verification the backend issues an access token (15 min TTL) and a refresh token (7 day TTL).',
      'The refresh token is stored in an HttpOnly, SameSite=Strict cookie scoped to /api/auth.',
    ],
    code: 'POST /api/auth/2fa/verify  →  { accessToken, user: { username } }',
  },
  {
    title: '3. Authenticated REST Requests',
    body: [
      'Every API request sends the access token as an Authorization: Bearer <token> header.',
      'The requireAuth middleware verifies the token with jwt.verify() and attaches req.user = { username, permissions }.',
      'Individual routes additionally call requirePerm("some.permission") to enforce fine-grained access control.',
      'Permissions are read from /etc/webadmin/permissions.yaml and cached in-process.',
    ],
    code: 'Authorization: Bearer <accessToken>',
  },
  {
    title: '4. Token Refresh',
    body: [
      'The axios client in the frontend intercepts 401 responses and calls POST /api/auth/refresh automatically.',
      'The refresh endpoint reads the HttpOnly cookie, verifies the refresh token, and issues a new access token.',
      'If the refresh token is expired or invalid the user is redirected to the login page.',
    ],
    code: 'POST /api/auth/refresh  →  { accessToken }  (reads HttpOnly cookie)',
  },
  {
    title: '5. WebSocket Connection — socket.io Auth',
    body: [
      'After login the frontend establishes a socket.io connection using { auth: { token: accessToken } }.',
      'The socketAuthMiddleware on the server verifies the token on connection and stores username + permissions in socket.data.',
      'The connection is rejected (disconnect) if the token is invalid or expired.',
    ],
    code: 'io({ auth: { token: accessToken } })',
  },
  {
    title: '6. Subscribing to Data Channels',
    body: [
      'Clients emit subscription events (e.g. "subscribe:stats", "subscribe:logs:<unitName>") after connecting.',
      'The server checks socket.data.permissions before starting the data stream.',
      'Live data (CPU, RAM, disk, service status, log lines) is pushed to subscribed clients as named events.',
    ],
    code: 'socket.emit("subscribe:stats")  →  socket.on("stats", handler)',
  },
  {
    title: '7. Hardware / System / Firewall Info',
    body: [
      'Dashboard stats (CPU, RAM, disk, load, uptime) are available via both REST (GET /api/stats) and the "stats" socket channel.',
      'Firewall rules are fetched via GET /api/firewall/status and pushed live on the "firewall" channel.',
      'System service status is fetched via GET /api/services and updated live on the "services" channel.',
    ],
    code: 'GET /api/stats  |  socket.on("stats", handler)',
  },
  {
    title: '8. Session Termination & Token Revocation',
    body: [
      'DELETE /api/auth/logout clears the refresh cookie server-side. The access token is short-lived and will expire within 15 minutes.',
      'For immediate revocation (e.g. deleting a user), the webadmin CLI can set tokens_valid_after in user state — any token with iat < that value is rejected.',
      'On the next 401, the frontend axios interceptor tries a refresh. If the refresh also fails, the user is redirected to login.',
    ],
    code: 'DELETE /api/auth/logout  →  clears cookie, client drops access token',
  },
];

export function FlowDoc() {
  return (
    <Box sx={{ maxWidth: 860 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Auth & Socket Flow Documentation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Internal reference for how authentication, token management, and real-time data channels work in WebAdmin.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sections.map((s) => (
          <Paper key={s.title} variant="outlined" sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              {s.title}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {s.body.map((line, i) => (
              <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
                {line}
              </Typography>
            ))}
            {s.code && (
              <Box
                component="pre"
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  m: 0,
                }}
              >
                {s.code}
              </Box>
            )}
          </Paper>
        ))}
      </Box>
    </Box>
  );
}
